import { UITooltip } from '../ui/component/tooltip';
import { UISVG, UISVGContent, $svg } from '../ui/svg';
import { UIPoint, UIRelPos, UIRect, UISize } from '../ui/types';
import { UIViewAddSpec } from '../ui/view';
import { CHChartElement } from './chart';
import { CHLegendItem } from './legend';
import { CHScale } from './scale';

export interface CHAxisOptions {
    title?: string;
}

export interface CHHitTestResult {
    distance: number;
    text: UIViewAddSpec;
    pos: UIPoint;
    rel_pos: UIRelPos;
}

export interface CHXYPlot<XValue, YValue> {
    minMax(): [[XValue, XValue], [YValue, YValue]];
    layout(rect: UIRect, x_scale: CHScale<XValue>, y_scale: CHScale<YValue>): void;
    render(svg: UISVG): void;
    hitTest(point: UIPoint | undefined): CHHitTestResult | undefined;
    fillLegend?(items: CHLegendItem[]): void;
    axes: CHXYAxes<XValue, YValue> | undefined;
    enabled: boolean;
}

export class CHXAxis<Value> implements UISVGContent {
    root: SVGGElement;
    scale: CHScale<Value>;
    private _options: CHAxisOptions;

    constructor(scale: CHScale<Value>, options: CHAxisOptions = {}) {
        this.root = $svg.G('ch-axis m-x');
        this.scale = scale;
        this._options = options;
    }

    layout(rect: UIRect) {
        const scale = this.scale;
        const options = this._options;

        this.root.setAttribute('transform', `translate(${rect[0]},${rect[3]})`);
        this.root.innerHTML = '';

        const tick_length = 6;
        const tick_padding = 4;
        const w = rect[2] - rect[0];
        const h = rect[3] - rect[1];
        const show_grid = scale.showGrid();
        const n_ticks = Math.max(2, Math.round(w / 150));
        const values = scale.ticks(n_ticks);

        let main_line = $svg.LINE(0, 0.25, w, 0.25);
        this.root.appendChild(main_line);

        for(const v of values) {
            let tick = $svg.G('ch-tick');
            const pos = Math.floor(scale.normalize(v) * w) + 0.25;
            tick.setAttribute('transform', `translate(${pos},0)`)
            let text = $svg.TEXT(scale.tickFormat(v), undefined, undefined, tick_length + tick_padding);
            text.setAttribute('dy', '0.71em');
            if(show_grid) {
                tick.appendChild($svg.LINE(0, 0, 0, -h, 'r-grid'));
                tick.appendChild($svg.LINE(0, 0, 0, tick_length));
            }
            tick.appendChild(text);
            this.root.appendChild(tick);
        }

        if(options.title) {
            let title = $svg.TEXT(options.title, 'r-title', rect[2] - rect[0] + 6, 0);
            title.setAttribute('dy', '0.34em');
            this.root.appendChild(title);
        }
    }
}

export class CHYAxis<Value> implements UISVGContent {
    root: SVGGElement;
    scale: CHScale<Value>;
    private _options: CHAxisOptions;

    constructor(scale: CHScale<Value>, options: CHAxisOptions = {}) {
        this.root = $svg.G('ch-axis m-y');

        this.scale = scale;
        this._options = options;
    }

    layout(rect: UIRect) {
        const scale = this.scale;
        const options = this._options;

        this.root.setAttribute('transform', `translate(${rect[0]},${rect[1]})`);
        this.root.innerHTML = '';

        const tick_length = 6;
        const tick_padding = 4;
        const n_ticks = 5;
        const values = scale.ticks(n_ticks);
        const show_grid = scale.showGrid();

        const w = rect[2] - rect[0];
        const h = rect[3] - rect[1];

        let main_line = $svg.LINE(0.25, 0, 0.25, h);
        this.root.appendChild(main_line);

        for(const v of values) {
            const tick = $svg.G('ch-tick');
            const pos = Math.floor((1 - scale.normalize(v)) * h) + 0.25;
            tick.setAttribute('transform', `translate(0,${pos})`)
            const text = $svg.TEXT(scale.tickFormat(v), undefined, -tick_length - tick_padding);
            text.setAttribute('dy', '0.32em');
            if(show_grid) {
                tick.appendChild($svg.LINE(0, 0, w, 0, 'r-grid'));
                tick.appendChild($svg.LINE(0, 0, -tick_length, 0));
            }
            tick.appendChild(text);
            this.root.appendChild(tick);
        }

        if(options.title) {
            let title = $svg.TEXT(options.title, 'r-title', 0, -10);
            this.root.appendChild(title);
        }
    }
}

export class CHXYAxes<XValue, YValue> implements CHChartElement {
    private _back: SVGGElement;
    private _x: CHXAxis<XValue>;
    private _y: CHYAxis<YValue>;
    private _rect!: UIRect;
    private _size!: UISize;

    private _plots: CHXYPlot<XValue, YValue>[];

    constructor(x: CHXAxis<XValue>, y: CHYAxis<YValue>) {
        this._x = x;
        this._y = y;
        this._plots = [];
        this._back = $svg.G('ch-back');
    }

    add(plot: CHXYPlot<XValue, YValue>) {
        plot.axes = this;
        this._plots.push(plot);
    }

    layout(size: UISize) {
        this._size = size;
        this._back.innerHTML = '';

        const x_scale = this._x.scale;
        const y_scale = this._y.scale;

        const min_max: [[XValue, XValue], [YValue, YValue]] = [[Infinity as any, -Infinity as any], [0 as any, -Infinity as any]];

        for(const p of this._plots) {
            if(!p.enabled) continue;
            const mm = p.minMax();
            if(mm[0][0] < min_max[0][0])
                min_max[0][0] = mm[0][0];
            if(mm[0][1] > min_max[0][1])
                min_max[0][1] = mm[0][1];
            if(mm[1][0] < min_max[1][0])
                min_max[1][0] = mm[1][0];
            if(mm[1][1] > min_max[1][1])
                min_max[1][1] = mm[1][1];
        }

        x_scale.scale(min_max[0][0], min_max[0][1]);
        y_scale.scale(min_max[1][0], min_max[1][1]);

        const margin: UIRect = [100, 20, 100, 30];
        const rect = this._rect = [margin[0], margin[1], size[0] - margin[2], size[1] - margin[3]];

        this._back.appendChild($svg.RECT(rect[0], rect[1], rect[2] - rect[0], rect[3] - rect[1]));

        this._x.layout(rect);
        this._y.layout(rect);

        for(const p of this._plots) {
            p.layout(rect, x_scale, y_scale);
        }
    }

    render(svg: UISVG) {
        svg.add(this._back);

        for(const p of this._plots) {
            p.render(svg);
        }

        svg.add(this._x.root);
        svg.add(this._y.root);
    }

    onMouse(point: UIPoint | undefined, tooltip: UITooltip) {
        let test: CHHitTestResult | undefined;

        if(point)
            point = [point[0] - this._rect[0], point[1] - this._rect[1]];

        for(const p of this._plots) {
            if(!p.enabled) continue;

            const t = p.hitTest(point);
            if(t && (!test || t.distance < test.distance))
                test = t;
        }

        if(test) {
            tooltip.content = test.text;
            tooltip.place(test.pos, test.rel_pos);
        } else {
            tooltip.ui_hide();
        }
    }

    onPlotEnabled(_plot: CHXYPlot<XValue, YValue>) {
        this.layout(this._size);
    }

    fillLegend(items: CHLegendItem[]) {
        for(const p of this._plots) {
            p.fillLegend && p.fillLegend(items);
        }
    }
}
